import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Camera, RotateCcw, ShieldAlert, CheckCircle2, Loader2, Upload } from "lucide-react";
import { api } from "../lib/api";

/**
 * CameraCapture — live camera capture for identity photos (DL front/back and
 * live selfie holding the DL). Backed primarily by `getUserMedia`, with a
 * `<input type="file" capture="environment">` fallback for iOS Safari /
 * in-app-browsers where `getUserMedia` is blocked. The `capture` attribute
 * still forces the OS camera app (no gallery pick) on mobile devices.
 *
 * Design decisions to make this bulletproof on iPhone + Android in Feb 2026:
 *   1. Camera does NOT auto-start. iOS Safari + many in-app browsers block
 *      `getUserMedia` unless triggered by a direct user tap. We show a big
 *      "Tap to start camera" button so the click IS the gesture.
 *   2. When `getUserMedia` fails, we fall through to `<input capture>` so
 *      the customer can still use their OS camera app to take a fresh photo.
 *      The gallery is not exposed thanks to the `capture` attribute.
 *   3. `facingMode` uses `{ideal: …}` (not `{exact}`) and we retry with a
 *      plain `{video: true}` when the first attempt throws NotFoundError or
 *      OverconstrainedError.
 *   4. Captured JPEG is downscaled to max 1600px wide at q=0.82 to keep the
 *      upload well below any ingress body-size limit and prevent multi-MB
 *      hangs on 4G/LTE.
 *   5. We do NOT set the multipart `Content-Type` header manually — axios
 *      lets the browser compute the boundary automatically. Setting it by
 *      hand strips the boundary and the backend can't parse the body.
 *
 * Props:
 *   - label / facingMode / uploadKind / captured / onCaptured / overlayHint / testId
 */
export default function CameraCapture({
  label,
  facingMode = "environment",
  uploadKind,
  captured,
  onCaptured,
  overlayHint,
  testId = "camera-capture",
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const readinessTimerRef = useRef(null);
  // idle | starting | ready | error | uploading
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");
  const [warnBlur, setWarnBlur] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  // Small helper — race any promise against a timeout so a wedged canvas,
  // decoder, or hung network request cannot leave the UI stuck forever.
  const withTimeout = (promise, ms, label) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(
      () => reject(new Error(`${label} timed out`)), ms
    )),
  ]);

  const stopStream = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    if (readinessTimerRef.current) {
      clearTimeout(readinessTimerRef.current);
      readinessTimerRef.current = null;
    }
  }, []);

  const startStream = useCallback(async () => {
    setStatus("starting");
    setErrMsg("");
    // Progressive constraint fallback: try the preferred camera first,
    // then relax to any camera, then bail out to the file-picker path.
    const attempts = [
      { audio: false, video: { facingMode: { ideal: facingMode }, width: { ideal: 1600 }, height: { ideal: 1200 } } },
      { audio: false, video: { facingMode: facingMode } },
      { audio: false, video: true },
    ];
    let s = null;
    let lastErr = null;
    for (const constraints of attempts) {
      try {
        s = await navigator.mediaDevices.getUserMedia(constraints);
        break;
      } catch (e) {
        lastErr = e;
      }
    }
    if (!s) {
      setStatus("error");
      const name = lastErr?.name || "";
      setErrMsg(
        name === "NotAllowedError" || name === "SecurityError"
          ? "Camera access was blocked. Tap the button below to use your device camera instead."
          : name === "NotFoundError"
            ? "No camera detected on this device. Tap below to pick your device camera."
            : name === "NotReadableError"
              ? "Your camera is in use by another app. Close it and try again, or use the button below."
              : (lastErr?.message || "Camera unavailable — use the device camera button below.")
      );
      return;
    }
    streamRef.current = s;
    const v = videoRef.current;
    if (v) {
      v.srcObject = s;
      v.setAttribute("playsinline", "true");
      v.setAttribute("muted", "true");
      try { await v.play(); } catch { /* iOS sometimes throws — safe to ignore */ }
    }
    // Readiness timeout: if the video track never delivers frames within 8s
    // (bad hardware, dead permission, silently-frozen stream) we abort and
    // punt the user to the OS-camera file-picker fallback instead of a
    // spinner that never resolves.
    if (readinessTimerRef.current) clearTimeout(readinessTimerRef.current);
    readinessTimerRef.current = setTimeout(() => {
      if (!v || !v.videoWidth || !v.videoHeight) {
        stopStream();
        setStatus("error");
        setErrMsg("Camera didn't respond in time. Tap below to use your device camera instead.");
      }
    }, 8000);
    // Prefer the actual "we have a frame" event when the browser fires it
    // (Chrome + modern Safari). If it lands before the timeout, we clear
    // the timer and mark ready.
    const markReady = () => {
      if (readinessTimerRef.current) {
        clearTimeout(readinessTimerRef.current);
        readinessTimerRef.current = null;
      }
      setStatus("ready");
    };
    if (v) {
      v.onloadedmetadata = markReady;
      v.oncanplay = markReady;
    }
    setStatus("ready");
  }, [facingMode, stopStream]);

  // Stop stream on unmount only. Camera never auto-starts — the user must
  // tap the shutter/start button (which we render below) to trigger the
  // media prompt. This satisfies iOS Safari's user-gesture requirement.
  useEffect(() => {
    return () => stopStream();
  }, [stopStream]);

  // Simple sharpness heuristic: sample center 200x200 pixels of the captured
  // frame, compute grayscale variance. Very low variance = blank / blurry.
  const isLikelyBlurry = (ctx, w, h) => {
    try {
      const sz = Math.min(200, w, h);
      const x = Math.floor((w - sz) / 2);
      const y = Math.floor((h - sz) / 2);
      const img = ctx.getImageData(x, y, sz, sz).data;
      let sum = 0, sumSq = 0, n = 0;
      for (let i = 0; i < img.length; i += 16) {
        const luma = 0.299 * img[i] + 0.587 * img[i + 1] + 0.114 * img[i + 2];
        sum += luma;
        sumSq += luma * luma;
        n++;
      }
      const mean = sum / n;
      const variance = sumSq / n - mean * mean;
      return variance < 400;
    } catch {
      return false;
    }
  };

  // Downscale a captured frame (or picked file) to keep the upload snappy on
  // mobile networks + safely under any body-size limit. Returns a Blob.
  const encodeJpeg = (source, maxW = 1600, quality = 0.82) =>
    new Promise((resolve, reject) => {
      const draw = (img, w, h) => {
        const scale = Math.min(1, maxW / w);
        const outW = Math.round(w * scale);
        const outH = Math.round(h * scale);
        const c = canvasRef.current || document.createElement("canvas");
        c.width = outW;
        c.height = outH;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, outW, outH);
        setWarnBlur(isLikelyBlurry(ctx, outW, outH));
        // Guard toBlob — Safari occasionally hangs on tab-inactive canvases.
        let done = false;
        const encodeGuard = setTimeout(() => {
          if (!done) reject(new Error("encode timed out"));
        }, 6000);
        c.toBlob((blob) => {
          done = true;
          clearTimeout(encodeGuard);
          blob ? resolve(blob) : reject(new Error("encode failed"));
        }, "image/jpeg", quality);
      };
      if (source instanceof HTMLVideoElement) {
        draw(source, source.videoWidth, source.videoHeight);
      } else if (source instanceof Blob) {
        const url = URL.createObjectURL(source);
        const img = new Image();
        let done = false;
        // Guard img.onload — some HEIC files decode extremely slowly on iOS.
        const decodeGuard = setTimeout(() => {
          if (!done) {
            URL.revokeObjectURL(url);
            reject(new Error("decode timed out"));
          }
        }, 6000);
        img.onload = () => {
          done = true; clearTimeout(decodeGuard); URL.revokeObjectURL(url);
          draw(img, img.naturalWidth, img.naturalHeight);
        };
        img.onerror = () => {
          done = true; clearTimeout(decodeGuard); URL.revokeObjectURL(url);
          reject(new Error("decode failed"));
        };
        img.src = url;
      } else {
        reject(new Error("unsupported source"));
      }
    });

  const doUpload = async (blob) => {
    setStatus("uploading");
    setUploadPct(0);
    const file = new File([blob], `${uploadKind}-${Date.now()}.jpg`, { type: "image/jpeg" });
    const fd = new FormData();
    fd.append("file", file);
    try {
      // Race the upload against a 30-second cap so a lost cellular connection
      // shows an actionable error instead of hanging the UI indefinitely.
      const { data } = await withTimeout(
        api.post(`/uploads/document/${uploadKind}`, fd, {
          onUploadProgress: (evt) => {
            if (evt.total) {
              setUploadPct(Math.min(99, Math.round((evt.loaded / evt.total) * 100)));
            }
          },
        }),
        30000, "Upload"
      );
      setUploadPct(100);
      const captured_at = new Date().toISOString();
      onCaptured({ ...data, captured_at, preview_url: URL.createObjectURL(blob) });
      stopStream();
      setStatus("idle");
      setUploadPct(0);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed — please try again";
      toast.error(msg);
      setStatus("ready");
      setUploadPct(0);
    }
  };

  const capture = async () => {
    const v = videoRef.current;
    if (!v || status !== "ready") return;
    if (!v.videoWidth || !v.videoHeight) return toast.error("Camera not ready yet — hold on");
    try {
      // Encode has its own internal guard, but wrap here too so any surprise
      // in the canvas pipeline resets the shutter instead of freezing it.
      const blob = await withTimeout(encodeJpeg(v), 8000, "Capture");
      await doUpload(blob);
    } catch (e) {
      toast.error(e?.message?.includes("timed out")
        ? "Capture took too long — please try again"
        : "Capture failed — try again");
      setStatus("ready");
    }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Only accept fresh camera captures. `<input capture>` on mobile opens
    // the OS camera app directly, so this is what we want.
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image");
      return;
    }
    try {
      const blob = await encodeJpeg(file);
      await doUpload(blob);
    } catch {
      toast.error("Could not process that photo — try again");
    } finally {
      // Reset input so picking the same file again re-fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const retake = () => {
    setWarnBlur(false);
    onCaptured(null); // clears parent → back to idle state
    setStatus("idle");
  };

  const showFileFallback = status === "error" || status === "idle";

  return (
    <div data-testid={testId} className="rounded-2xl border border-cyan/25 bg-cyan/[0.03] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="label-caps text-cyan text-[10px] flex items-center gap-1.5">
          <Camera size={11} /> {label}
        </div>
        {captured?.file_id && (
          <span className="label-caps text-[10px] text-lime-300 flex items-center gap-1">
            <CheckCircle2 size={11} /> Captured
          </span>
        )}
      </div>

      <div className="relative aspect-[3/2] rounded-xl overflow-hidden bg-black border border-white/10">
        {captured?.file_id ? (
          <img
            src={captured.preview_url || `/api/admin/uploads/${captured.file_id}`}
            alt={label}
            className="w-full h-full object-cover"
            data-testid={`${testId}-preview`}
          />
        ) : (
          <>
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              muted
              autoPlay
              data-testid={`${testId}-video`}
            />
            {overlayHint && status === "ready" && (
              <div className="absolute inset-x-0 bottom-2 mx-2 rounded-lg bg-black/60 backdrop-blur-sm text-white text-[11px] px-3 py-1.5 text-center">
                {overlayHint}
              </div>
            )}
            {status === "idle" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/50 p-4 text-center">
                <Camera size={26} className="text-cyan" />
                <div className="text-white/85 text-xs max-w-[16rem]">
                  We need a live photo — tap below to open your camera.
                </div>
                <button
                  type="button"
                  data-testid={`${testId}-start`}
                  onClick={startStream}
                  className="px-4 py-2 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110 inline-flex items-center gap-1.5"
                >
                  <Camera size={12} /> Tap to start camera
                </button>
              </div>
            )}
            {status === "starting" && (
              <div className="absolute inset-0 flex items-center justify-center text-white/70 text-xs gap-2 bg-black/50">
                <Loader2 className="animate-spin" size={16} /> Starting camera…
              </div>
            )}
            {status === "error" && (
              <div className="absolute inset-0 p-4 flex flex-col items-center justify-center text-center text-red-200 text-xs gap-2 bg-red-500/20 backdrop-blur-sm">
                <ShieldAlert size={20} />
                <div className="max-w-[18rem]">{errMsg}</div>
                <button
                  type="button"
                  data-testid={`${testId}-retry`}
                  onClick={startStream}
                  className="mt-1 px-3 py-1.5 rounded-full text-[10px] label-caps border border-white/25 text-white hover:border-cyan/60"
                >
                  Try live camera again
                </button>
              </div>
            )}
          </>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {warnBlur && captured?.file_id && (
        <div className="rounded-lg bg-amber-400/10 border border-amber-400/30 px-3 py-2 text-[11px] text-amber-200 flex items-start gap-2">
          <ShieldAlert size={12} className="mt-0.5 flex-shrink-0" />
          <span>Image may be blurry or dark. Please retake for a clearer capture.</span>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {captured?.file_id ? (
          <button
            type="button"
            data-testid={`${testId}-retake`}
            onClick={retake}
            className="flex-1 py-2.5 rounded-full text-xs label-caps border border-white/20 text-white/80 hover:text-white hover:border-cyan/50 inline-flex items-center justify-center gap-1.5"
          >
            <RotateCcw size={12} /> Retake
          </button>
        ) : status === "ready" ? (
          <button
            type="button"
            data-testid={`${testId}-shutter`}
            disabled={status === "uploading"}
            onClick={capture}
            className="flex-1 py-2.5 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
          >
            {status === "uploading" ? (
              <><Loader2 size={12} className="animate-spin" /> Uploading…</>
            ) : (
              <><Camera size={12} /> Capture</>
            )}
          </button>
        ) : status === "uploading" ? (
          <button
            type="button"
            disabled
            data-testid={`${testId}-uploading`}
            className="flex-1 py-2.5 rounded-full text-xs label-caps bg-cyan/60 text-midnight font-semibold inline-flex items-center justify-center gap-1.5 relative overflow-hidden"
          >
            <span
              aria-hidden
              className="absolute inset-y-0 left-0 bg-cyan/60 transition-[width] duration-300"
              style={{ width: `${uploadPct}%` }}
            />
            <span className="relative inline-flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" />
              Uploading… {uploadPct > 0 ? `${uploadPct}%` : ""}
            </span>
          </button>
        ) : null}

        {/* Device-camera fallback — the `capture` attribute forces the OS
            camera app on iPhone / Android (no library picker), so it still
            enforces "must be freshly captured, not chosen from photos".
            Shown whenever the live stream isn't available. */}
        {showFileFallback && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture={facingMode === "user" ? "user" : "environment"}
              onChange={onFilePicked}
              className="hidden"
              data-testid={`${testId}-file-input`}
            />
            <button
              type="button"
              data-testid={`${testId}-file`}
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 py-2.5 rounded-full text-xs label-caps border border-cyan/40 text-cyan hover:bg-cyan/10 inline-flex items-center justify-center gap-1.5"
            >
              <Upload size={12} /> Use device camera
            </button>
          </>
        )}
      </div>

      {captured?.captured_at && (
        <div className="text-[10px] text-white/40 text-center">
          Captured {new Date(captured.captured_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}
